import { Loader } from "components";
import Panel from "components/myCoachConnectPanel/Panel";
import Navbar from "components/Navbar/NewNavbar";
import { useAuth } from "context/auth";
import { lazyWithRetry } from "helpers/lazyWithRetry";
import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Switch, useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import styled from "styled-components";

import { UserStoreProvider } from "utils/GlobalState";

const MainContainer = styled.div`
  display: flex;
  position: relative;
  //background: #f8f9fa;
  width: 100%;
  max-width: 100rem;
  margin: 0 auto;
  flex-direction: row;
  justify-content: center;
  padding-bottom: 4rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const PublicRoute = lazyWithRetry(() => import("routes/PublicRoute"));
const PrivateRoute = lazyWithRetry(() => import("routes/PrivateRoute"));

const AccountEditScreen = lazyWithRetry(() =>
  import("screens/AccountEditScreen")
);
const AccountScreen = lazyWithRetry(() => import("screens/AccountScreen"));
const ArticleScreen = lazyWithRetry(() => import("screens/ArticleScreen"));
const CompanyPolicyScreen = lazyWithRetry(() =>
  import("screens/CompanyPolicyScreen")
);
const Dashboard = lazyWithRetry(() => import("screens/Dashboard"));
const ExploreScreen = lazyWithRetry(() => import("screens/ExploreScreen"));
const ForgotPasswordScreen = lazyWithRetry(() =>
  import("screens/ForgotPasswordScreen")
);
const GPPrivacyScreen = lazyWithRetry(() => import("screens/GPPrivacyScreen"));
const GPTermsScreen = lazyWithRetry(() => import("screens/GPTermsScreen"));
const LessonScreen = lazyWithRetry(() => import("screens/LessonScreen"));
const LoginScreen = lazyWithRetry(() => import("screens/LoginScreen"));
const MyInsightsScreen = lazyWithRetry(() =>
  import("screens/MyInsightsScreen")
);
const MyProgramScreen = lazyWithRetry(() => import("screens/MyProgramScreen"));
const LabScreen = lazyWithRetry(() => import("screens/LabScreen"));
const MyProgramEditScreen = lazyWithRetry(() =>
  import("screens/MyProgramEditScreen")
);
const NotFound = lazyWithRetry(() => import("screens/404Screen"));
const OurStories = lazyWithRetry(() => import("screens/OurStories"));
const PostRegistrationPersonaPickerScreen = lazyWithRetry(() =>
  import("screens/PostRegistrationPersonaPickerScreen")
);
const ProfessionalVisionScreen = lazyWithRetry(() =>
  import("screens/ProfessionalVisionScreen")
);
/* const RegisterScreen = lazyWithRetry(() => import("screens/RegisterScreen")); */

const NewProfileScreen = lazyWithRetry(() =>
  import("screens/NewProfileScreen")
);

const NewRegistrationScreen = lazyWithRetry(() =>
  import("screens/NewRegistrationScreen")
);

const CustomRegisterScreen = lazyWithRetry(() =>
  import("screens/CustomRegisterScreen")
);
const PlatinumMemberRegisterScreen = lazyWithRetry(() =>
  import("screens/PlatinumMemberRegisterScreen")
);

const EventsScreen = lazyWithRetry(() => import("screens/EventsScreen"));

const EmpowerMeScreen = lazyWithRetry(() => import("screens/EmpowerMeScreen"));

const VerifyEmailScreen = lazyWithRetry(() =>
  import("screens/VerifyEmailScreen")
);

const StoriesFormScreen = lazyWithRetry(() =>
  import("screens/StoriesFormScreen")
);
const StoryScreen = lazyWithRetry(() => import("screens/StoryScreen"));

const SamlAuthorisationScreen = lazyWithRetry(() =>
  import("screens/SamlAuthorisationScreen")
);

const PasswordResetScreen = lazyWithRetry(() =>
  import("screens/PasswordResetScreen")
);

const ZoomMeetingFullScreen = lazyWithRetry(() =>
  import("screens/ZoomMeetingFullScreen")
);

const ManageAllCoachingSessions = lazyWithRetry(() =>
  import("components/myCoachConnectPanel/ManageAllCoachingSessions")
);

const ProfessionalVisionPDF = lazyWithRetry(() =>
  import("components/ProfessionalVisionPDF")
);

function RoutesInner() {
  const authContext = useAuth();
  const hasAuth = authContext?.userObject?.id;
  const history = useHistory();

  useEffect(() => {
    const user = authContext?.userObject;
    if (hasAuth && !!user?.new_registration_pending) {
      history.push("/register", { resumeUser: user });
      return;
    }
  }, [hasAuth, history, authContext]);

  const location = useLocation();
  const hidePanelRoutes = [
    "/login",
    "/password",
    "/reset",
    "/events",
    "/register",
    //"/empower-me",
    "/custom-register",
    "/platinum-member-register",
    "/verify-email",
    "/saml-authorisation",
    "/privacy-statement",
    "/terms-and-conditions",
    "/account",
    "/profile",
    "/version",
    "/manage-sessions",
    "/zoom",
  ];
  const shouldShowPanel = !hidePanelRoutes.some((route) =>
    location.pathname.startsWith(route)
  );
  const hideNavbar =
    ["/login", "/password", "/reset", "/register"].some((r) =>
      location.pathname.startsWith(r)
    ) || location.pathname.startsWith("/zoom");
  return (
    <Suspense fallback={<Loader />}>
      {!!hasAuth && !hideNavbar && <Navbar />}
      <MainContainer>
        <Switch>
          <PublicRoute path="/login">
            <LoginScreen />
          </PublicRoute>
          <PublicRoute path="/password">
            <ForgotPasswordScreen />
          </PublicRoute>
          <PublicRoute path="/reset">
            <PasswordResetScreen />
          </PublicRoute>
          <PublicRoute exact path="/register">
            <NewRegistrationScreen />
            {/* <RegisterScreen /> */}
          </PublicRoute>

          <PublicRoute exact path="/custom-register">
            <UserStoreProvider>
              <CustomRegisterScreen />
            </UserStoreProvider>
          </PublicRoute>
          <PublicRoute exact path="/platinum-member-register">
            <UserStoreProvider>
              <PlatinumMemberRegisterScreen />
            </UserStoreProvider>
          </PublicRoute>
          <PublicRoute path="/verify-email">
            <VerifyEmailScreen />
          </PublicRoute>
          <PublicRoute path="/saml-authorisation">
            <SamlAuthorisationScreen />
          </PublicRoute>
          <PublicRoute exact path="/privacy-statement">
            <GPPrivacyScreen />
          </PublicRoute>
          <PublicRoute exact path="/terms-and-conditions">
            <GPTermsScreen />
          </PublicRoute>
          <PrivateRoute exact path="/profile">
            <UserStoreProvider>
              <NewProfileScreen />
            </UserStoreProvider>
          </PrivateRoute>
          <PrivateRoute exact path="/empower-me">
            <UserStoreProvider>
              <EmpowerMeScreen />
            </UserStoreProvider>
          </PrivateRoute>
          <PrivateRoute exact path="/">
            <Dashboard />
          </PrivateRoute>
          <PrivateRoute exact path="/manage-sessions">
            <ManageAllCoachingSessions />
          </PrivateRoute>
          <PrivateRoute exact path="/zoom/j/:meetingNumber">
            <ZoomMeetingFullScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/lesson/:step">
            <LessonScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/my-insights">
            <MyInsightsScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/program">
            <MyProgramScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/labs/:slug">
            <LabScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/program/personalise">
            <MyProgramEditScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/account">
            <AccountScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/account/edit">
            <AccountEditScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/explore">
            <ExploreScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/events">
            <EventsScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/company-policy">
            <CompanyPolicyScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/blogpost/:slug">
            <ArticleScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/our-stories">
            <OurStories />
          </PrivateRoute>
          <PrivateRoute exact path="/stories/:slug">
            <StoryScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/stories-form/:slug">
            <StoriesFormScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/current-situation">
            <PostRegistrationPersonaPickerScreen />
          </PrivateRoute>
          <PrivateRoute exact path="/my-insights/your-professional-vision">
            <ProfessionalVisionScreen />
          </PrivateRoute>
          <PrivateRoute
            exact
            path="/my-insights/professional-vision-pdf-viewer"
          >
            <ProfessionalVisionPDF />
          </PrivateRoute>

          <PublicRoute exact path="/version">
            {() => {
              return <>{process.env.REACT_APP_VERSION}</>;
            }}
          </PublicRoute>
          <PublicRoute path="*">
            <NotFound />
          </PublicRoute>
        </Switch>
        {shouldShowPanel && !!hasAuth && <Panel />}
      </MainContainer>
    </Suspense>
  );
}

export default function Routes() {
  return (
    <Router>
      <RoutesInner />
    </Router>
  );
}
